import React, { useState, useEffect } from 'react';
import { toast } from 'react-hot-toast';
import { 
  Megaphone, 
  Eye, 
  Save, 
  FileText, 
  Landmark, 
  Scale, 
  Image as ImageIcon, 
  Sparkles, 
  CheckCircle2, 
  XCircle,
  ExternalLink,
  RotateCcw
} from 'lucide-react';
import comunicadoService from '../services/comunicadoService';
import HomenajeModal from '../components/HomenajeModal';

const VALORES_POR_DEFECTO = {
  activo: true,
  tipo_icono: 'tramite',
  foto_url: '',
  titulo: 'Modernización y Trámites 100% en Línea',
  subtitulo: 'CBR San Carlos · Atención Ágil, Segura y Sin Filas',
  parrafo_1: 'Estimada comunidad: en el marco de la modernización institucional del Conservador de Bienes Raíces de San Carlos y en cumplimiento de la Ley N° 21.772, ponemos a su disposición nuestro Módulo de Trámites y Solicitudes en Línea. Ahora puede ingresar sus solicitudes de copias, certificados e inscripciones directamente desde nuestra plataforma web, sin necesidad de traslados ni esperas presenciales.',
  parrafo_2: 'A través de nuestra plataforma, podrá realizar su trámite de manera íntegra y 100% digital, hacer el seguimiento de su solicitud en todo momento y recibir sus documentos oficiales directamente por esta vía digital si así lo desea.',
  destacado: 'Asimismo, le invitamos a preferir la atención a través de nuestro portal web. Los trámites ingresados por este canal cuentan con gestión prioritaria, avisos automáticos a su correo electrónico y plena validez legal para todo tipo de gestiones notariales, bancarias y judiciales.',
  boton_texto: 'Iniciar Trámite en Línea',
  boton_url: '/solicitudes',
  mostrar_boton_secundario: true,
  boton_secundario_texto: 'Continuar al Sitio Web'
};

export default function AdminComunicado() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(VALORES_POR_DEFECTO);
  const [mostrarPreview, setMostrarPreview] = useState(false);

  useEffect(() => {
    cargarDatos();
  }, []);

  const cargarDatos = async () => {
    try {
      setLoading(true);
      const data = await comunicadoService.get();
      if (data) {
        setForm({
          ...VALORES_POR_DEFECTO,
          ...data,
          foto_url: data.foto_url || ''
        });
      }
    } catch (error) {
      toast.error('Error al cargar la configuración del comunicado.');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
  };

  const handleGuardar = async (e) => {
    if (e) e.preventDefault();
    if (!form.titulo.trim() || !form.parrafo_1.trim()) {
      toast.error('El título y al menos el primer párrafo son obligatorios.');
      return;
    }

    setSaving(true);
    const toastId = toast.loading('Guardando configuración...');
    try {
      await comunicadoService.update(form);
      toast.success('Comunicado institucional guardado con éxito.', { id: toastId });
    } catch (error) {
      toast.error(error.response?.data?.message || 'Error al guardar los cambios.', { id: toastId });
    } finally {
      setSaving(false);
    }
  };

  const handleRestablecerDefecto = () => {
    if (window.confirm('¿Desea restablecer el texto promocional por defecto para el Módulo de Trámites?')) {
      setForm(VALORES_POR_DEFECTO);
      toast.success('Valores restablecidos. Recuerde guardar los cambios.');
    }
  };

  if (loading) {
    return (
      <div style={{ padding: '3rem 1rem', textAlign: 'center', minHeight: '60vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
        <div className="spinner spinner-lg" />
        <p style={{ marginTop: '1rem', color: '#64748b', fontWeight: 600 }}>Cargando mantenedor de comunicado...</p>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: '1000px', margin: '0 auto', padding: '2rem 1.25rem 5rem 1.25rem' }}>
      {/* ── Encabezado Principal ── */}
      <div style={{ marginBottom: '2rem', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '1rem' }}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '6px' }}>
            <span style={{ background: '#eff6ff', color: '#1e40af', padding: '6px 10px', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Megaphone size={20} />
            </span>
            <span style={{ fontSize: '0.8rem', fontWeight: 700, color: '#1e40af', textTransform: 'uppercase', letterSpacing: '0.6px' }}>
              Panel de Administración
            </span>
          </div>
          <h1 style={{ fontSize: '1.85rem', fontWeight: 800, color: '#0f172a', margin: 0 }}>
            Mantenedor de Comunicado Institucional
          </h1>
          <p style={{ color: '#64748b', fontSize: '0.95rem', marginTop: '6px', margin: 0 }}>
            Configure el modal de aviso que se despliega automáticamente a los ciudadanos al ingresar a la plataforma.
          </p>
        </div>

        <div style={{ display: 'flex', gap: '10px' }}>
          <button
            type="button"
            onClick={handleRestablecerDefecto}
            className="btn btn-secondary"
            style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', fontSize: '0.85rem' }}
            title="Restablecer plantilla de trámites"
          >
            <RotateCcw size={15} />
            <span>Plantilla Trámites</span>
          </button>

          <button
            type="button"
            onClick={() => setMostrarPreview(true)}
            className="btn btn-secondary"
            style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', fontSize: '0.88rem', fontWeight: 700, borderColor: '#bfdbfe', background: '#eff6ff', color: '#1d4ed8' }}
          >
            <Eye size={16} />
            <span>Previsualizar Modal</span>
          </button>
        </div>
      </div>

      <form onSubmit={handleGuardar} style={{ display: 'flex', flexDirection: 'column', gap: '1.75rem' }}>
        
        {/* ── 1. Interruptor de Estado (Mostrar / Ocultar) ── */}
        <section className="glass-card" style={{ padding: '1.5rem', borderLeft: form.activo ? '5px solid #10b981' : '5px solid #94a3b8' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '1rem' }}>
            <div>
              <h2 style={{ fontSize: '1.15rem', fontWeight: 700, margin: 0, color: '#0f172a', display: 'flex', alignItems: 'center', gap: '8px' }}>
                {form.activo ? <CheckCircle2 size={20} color="#10b981" /> : <XCircle size={20} color="#94a3b8" />}
                Estado de Visibilidad Pública
              </h2>
              <p style={{ margin: 0, marginTop: '4px', fontSize: '0.85rem', color: '#64748b' }}>
                {form.activo 
                  ? 'El comunicado se encuentra ACTIVO y se mostrará a los usuarios al ingresar al sitio.' 
                  : 'El comunicado se encuentra DESACTIVADO (No se mostrará el modal en la web pública).'}
              </p>
            </div>

            <label style={{ display: 'flex', alignItems: 'center', gap: '12px', cursor: 'pointer', background: form.activo ? '#ecfdf5' : '#f1f5f9', padding: '10px 18px', borderRadius: '30px', border: form.activo ? '1px solid #a7f3d0' : '1px solid #cbd5e1', transition: 'all 0.2s' }}>
              <input
                type="checkbox"
                checked={form.activo}
                onChange={(e) => handleInputChange('activo', e.target.checked)}
                style={{ width: '20px', height: '20px', accentColor: '#10b981', cursor: 'pointer' }}
              />
              <span style={{ fontWeight: 700, fontSize: '0.92rem', color: form.activo ? '#065f46' : '#475569' }}>
                {form.activo ? 'Publicado (Activo)' : 'Oculto (Inactivo)'}
              </span>
            </label>
          </div>
        </section>

        {/* ── 2. Cabecera e Identidad Visual ── */}
        <section className="glass-card" style={{ padding: '1.75rem' }}>
          <h2 style={{ fontSize: '1.15rem', fontWeight: 700, color: '#0f172a', marginBottom: '1.25rem', borderBottom: '1px solid #e2e8f0', paddingBottom: '0.75rem' }}>
            1. Cabecera e Ícono Superior
          </h2>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '12px', marginBottom: '1.25rem' }}>
            {[
              { id: 'tramite', label: 'Trámites en Línea', icon: FileText, desc: 'Ícono de gestión digital' },
              { id: 'institucional', label: 'Institucional', icon: Landmark, desc: 'Escudo oficial del CBR' },
              { id: 'ley', label: 'Normativa / Ley', icon: Scale, desc: 'Balanza de la justicia' },
              { id: 'foto', label: 'Foto / Avatar', icon: ImageIcon, desc: 'Imagen o foto personalizada' },
            ].map(item => {
              const IconComp = item.icon;
              const seleccionado = form.tipo_icono === item.id;
              return (
                <button
                  key={item.id}
                  type="button"
                  onClick={() => handleInputChange('tipo_icono', item.id)}
                  style={{
                    padding: '14px',
                    borderRadius: '10px',
                    border: seleccionado ? '2px solid #1a1e38' : '1px solid #e2e8f0',
                    background: seleccionado ? '#f8fafc' : '#ffffff',
                    cursor: 'pointer',
                    textAlign: 'center',
                    boxShadow: seleccionado ? '0 4px 12px rgba(26, 30, 56, 0.08)' : 'none',
                    transition: 'all 0.2s'
                  }}
                >
                  <div style={{ width: '40px', height: '40px', borderRadius: '50%', background: seleccionado ? '#1a1e38' : '#f1f5f9', color: seleccionado ? '#d4af37' : '#64748b', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 8px auto' }}>
                    <IconComp size={20} />
                  </div>
                  <div style={{ fontWeight: 700, fontSize: '0.9rem', color: '#1e293b' }}>{item.label}</div>
                  <div style={{ fontSize: '0.75rem', color: '#64748b', marginTop: '2px' }}>{item.desc}</div>
                </button>
              );
            })}
          </div>

          {form.tipo_icono === 'foto' && (
            <div className="form-group" style={{ marginTop: '1rem', background: '#f8fafc', padding: '14px', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
              <label style={{ fontWeight: 600, fontSize: '0.88rem', color: '#1e293b', marginBottom: '6px', display: 'block' }}>
                URL de la Imagen o Fotografía:
              </label>
              <input
                type="text"
                placeholder="https://ejemplo.com/foto_homenaje.jpg (Dejar vacío para imagen de archivo)"
                value={form.foto_url}
                onChange={(e) => handleInputChange('foto_url', e.target.value)}
                style={{ width: '100%', padding: '9px 12px', borderRadius: '6px', border: '1px solid #cbd5e1' }}
              />
              <span style={{ fontSize: '0.74rem', color: '#64748b', marginTop: '4px', display: 'block' }}>
                Si se deja vacío, utilizará la foto oficial de homenaje por defecto.
              </span>
            </div>
          )}
        </section>

        {/* ── 3. Título y Párrafos del Comunicado ── */}
        <section className="glass-card" style={{ padding: '1.75rem' }}>
          <h2 style={{ fontSize: '1.15rem', fontWeight: 700, color: '#0f172a', marginBottom: '1.25rem', borderBottom: '1px solid #e2e8f0', paddingBottom: '0.75rem' }}>
            2. Mensaje y Redacción
          </h2>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
            <div className="form-group">
              <label style={{ fontWeight: 700, fontSize: '0.9rem', color: '#1e293b', marginBottom: '6px', display: 'block' }}>
                Título Principal del Modal <span style={{ color: '#ef4444' }}>*</span>
              </label>
              <input
                type="text"
                placeholder="Ej: Modernización y Trámites 100% en Línea"
                value={form.titulo}
                onChange={(e) => handleInputChange('titulo', e.target.value)}
                required
                style={{ width: '100%', padding: '11px 14px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '1.05rem', fontWeight: 600 }}
              />
            </div>

            <div className="form-group">
              <label style={{ fontWeight: 600, fontSize: '0.88rem', color: '#1e293b', marginBottom: '6px', display: 'block' }}>
                Subtítulo o Bajada Institucional (Opcional):
              </label>
              <input
                type="text"
                placeholder="Ej: CBR San Carlos · Atención Ágil, Segura y Sin Filas"
                value={form.subtitulo}
                onChange={(e) => handleInputChange('subtitulo', e.target.value)}
                style={{ width: '100%', padding: '9px 12px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '0.92rem' }}
              />
            </div>

            <div className="form-group">
              <label style={{ fontWeight: 700, fontSize: '0.9rem', color: '#1e293b', marginBottom: '6px', display: 'block' }}>
                Primer Párrafo del Mensaje <span style={{ color: '#ef4444' }}>*</span>
              </label>
              <textarea
                rows={4}
                placeholder="Texto introductorio del comunicado..."
                value={form.parrafo_1}
                onChange={(e) => handleInputChange('parrafo_1', e.target.value)}
                required
                style={{ width: '100%', padding: '12px 14px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '0.95rem', lineHeight: '1.6' }}
              />
            </div>

            <div className="form-group">
              <label style={{ fontWeight: 600, fontSize: '0.88rem', color: '#1e293b', marginBottom: '6px', display: 'block' }}>
                Segundo Párrafo (Opcional):
              </label>
              <textarea
                rows={3}
                placeholder="Párrafo secundario o de detalles..."
                value={form.parrafo_2}
                onChange={(e) => handleInputChange('parrafo_2', e.target.value)}
                style={{ width: '100%', padding: '12px 14px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '0.95rem', lineHeight: '1.6' }}
              />
            </div>
          </div>
        </section>

        {/* ── 4. Cuadro Destacado (Estilo Foto 2: Azul + Borde Dorado) ── */}
        <section className="glass-card" style={{ padding: '1.75rem' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1.25rem', borderBottom: '1px solid #e2e8f0', paddingBottom: '0.75rem' }}>
            <h2 style={{ fontSize: '1.15rem', fontWeight: 700, color: '#0f172a', margin: 0, display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Sparkles size={18} color="#d4af37" />
              3. Cuadro Destacado (Callout con Barra Dorada)
            </h2>
            <span style={{ fontSize: '0.74rem', background: '#fef3c7', color: '#92400e', fontWeight: 700, padding: '3px 8px', borderRadius: '4px' }}>
              Alta Visibilidad
            </span>
          </div>

          <p style={{ fontSize: '0.85rem', color: '#64748b', marginTop: 0, marginBottom: '1rem' }}>
            Este cuadro aparece resaltado en el modal con el fondo azul marino institucional y el borde dorado distintivo. Es ideal para avisos legales, horarios o invitaciones clave.
          </p>

          <div className="form-group" style={{ marginBottom: '1.25rem' }}>
            <label style={{ fontWeight: 600, fontSize: '0.88rem', color: '#1e293b', marginBottom: '6px', display: 'block' }}>
              Texto del Cuadro Destacado:
            </label>
            <textarea
              rows={3}
              placeholder="Ej: Asimismo, le invitamos a preferir la atención a través de nuestro portal web..."
              value={form.destacado}
              onChange={(e) => handleInputChange('destacado', e.target.value)}
              style={{ width: '100%', padding: '12px 14px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '0.95rem', lineHeight: '1.6' }}
            />
          </div>

          {/* Previsualización en tiempo real del cuadro */}
          {form.destacado && (
            <div>
              <span style={{ fontSize: '0.75rem', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', marginBottom: '6px', display: 'block' }}>
                Vista previa del cuadro destacado:
              </span>
              <div style={{
                padding: '1.15rem 1.35rem',
                backgroundColor: '#1a1e38',
                borderLeft: '4px solid #d4af37',
                borderRadius: '0 8px 8px 0',
                color: '#ffffff',
                fontSize: '0.94rem',
                lineHeight: '1.65',
                boxShadow: '0 4px 12px rgba(26, 30, 56, 0.15)'
              }}>
                {form.destacado}
              </div>
            </div>
          )}
        </section>

        {/* ── 5. Botones de Acción (Call to Action) ── */}
        <section className="glass-card" style={{ padding: '1.75rem' }}>
          <h2 style={{ fontSize: '1.15rem', fontWeight: 700, color: '#0f172a', marginBottom: '1.25rem', borderBottom: '1px solid #e2e8f0', paddingBottom: '0.75rem' }}>
            4. Botones y Llamados a la Acción
          </h2>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '1.25rem' }}>
            <div className="form-group">
              <label style={{ fontWeight: 600, fontSize: '0.88rem', color: '#1e293b', marginBottom: '6px', display: 'block' }}>
                Texto del Botón Principal (Acción):
              </label>
              <input
                type="text"
                placeholder="Ej: Iniciar Trámite en Línea"
                value={form.boton_texto}
                onChange={(e) => handleInputChange('boton_texto', e.target.value)}
                style={{ width: '100%', padding: '9px 12px', borderRadius: '8px', border: '1px solid #cbd5e1' }}
              />
            </div>

            <div className="form-group">
              <label style={{ fontWeight: 600, fontSize: '0.88rem', color: '#1e293b', marginBottom: '6px', display: 'block' }}>
                Enlace de Destino (Ruta o URL):
              </label>
              <input
                type="text"
                placeholder="Ej: /solicitudes o /ingresotramite"
                value={form.boton_url}
                onChange={(e) => handleInputChange('boton_url', e.target.value)}
                style={{ width: '100%', padding: '9px 12px', borderRadius: '8px', border: '1px solid #cbd5e1' }}
              />
            </div>
          </div>

          <div style={{ marginTop: '1.25rem', paddingTop: '1.25rem', borderTop: '1px dashed #e2e8f0', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '1rem' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={form.mostrar_boton_secundario}
                onChange={(e) => handleInputChange('mostrar_boton_secundario', e.target.checked)}
                style={{ width: '18px', height: '18px', accentColor: '#1a1e38' }}
              />
              <span style={{ fontSize: '0.9rem', color: '#334155', fontWeight: 600 }}>
                Mostrar botón secundario de cierre
              </span>
            </label>

            {form.mostrar_boton_secundario && (
              <div style={{ width: '250px' }}>
                <input
                  type="text"
                  placeholder="Ej: Continuar al Sitio Web"
                  value={form.boton_secundario_texto}
                  onChange={(e) => handleInputChange('boton_secundario_texto', e.target.value)}
                  style={{ width: '100%', padding: '7px 12px', borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '0.88rem' }}
                />
              </div>
            )}
          </div>
        </section>

        {/* ── Barra de Guardado ── */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '1rem', flexWrap: 'wrap', gap: '1rem' }}>
          <button
            type="button"
            onClick={() => setMostrarPreview(true)}
            className="btn btn-secondary"
            style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', padding: '12px 24px', fontSize: '0.95rem' }}
          >
            <Eye size={18} />
            <span>Previsualizar Modal en Pantalla</span>
          </button>

          <button
            type="submit"
            disabled={saving}
            className="btn btn-primary btn-lg"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '10px',
              padding: '12px 36px',
              fontSize: '1rem',
              fontWeight: 700,
              backgroundColor: '#1a1e38',
              borderColor: '#d4af37'
            }}
          >
            <Save size={18} color="#d4af37" />
            <span>{saving ? 'Guardando...' : 'Guardar y Publicar Cambios'}</span>
          </button>
        </div>

      </form>

      {/* ── Modal de Previsualización en Vivo ── */}
      {mostrarPreview && (
        <HomenajeModal
          isPreview={true}
          datosPreview={form}
          onClose={() => setMostrarPreview(false)}
        />
      )}
    </div>
  );
}
